import { useAsyncData, useRoute } from '#imports'
import { computed, ref, watch } from 'vue'

import type { Review } from '@backmarket/http-api/src/api-specs-reviews/types/review'
import { $httpFetch } from '@backmarket/nuxt-module-http/$httpFetch'
import { useLogger } from '@backmarket/nuxt-module-logger/useLogger'
import { insertIf } from '@backmarket/utils/collection/insertIf'
import { isEmpty } from '@backmarket/utils/object/isEmpty'

import { useRequests } from './useRequests'

function fetchReviews(page = 1) {
  const route = useRoute()
  const requests = useRequests()

  if (!requests || !('getReviews' in requests))
    return Promise.resolve({
      results: <Review[]>[],
      next: '',
      previous: '',
    })

  return $httpFetch(requests.getReviews, {
    pathParams: route.params,
    queryParams: {
      ...route.query,
      ...insertIf(page > 1, { page }),
    },
  })
}

function logError(error: Error) {
  const route = useRoute()
  const logger = useLogger()

  if (error instanceof Error) {
    logger.error(`[REVIEWS][${route.name?.toString()}]`, {
      error,
    })
  }
}

export function useReviewsList() {
  const route = useRoute()

  const page = ref(1)
  const isFetchMorePending = ref(false)
  const hasMoreReviews = ref(true)
  const moreReviews = ref<Review[]>([])

  const {
    data,
    error: fetchReviewsError,
    pending,
    execute,
  } = useAsyncData(() => fetchReviews())

  const isInitialFetchPending = computed(() => pending.value)

  const canFetchMoreReviews = computed(() => {
    return page.value > 1 ? hasMoreReviews.value : !isEmpty(data.value?.next)
  })

  const reviewsResponse = computed(() => {
    if (page.value > 1 && !isEmpty(moreReviews.value)) {
      return [...(data.value?.results ?? []), ...moreReviews.value]
    }

    return data.value?.results ?? []
  })

  watch(
    fetchReviewsError,
    () => {
      if (fetchReviewsError.value instanceof Error) {
        logError(fetchReviewsError.value)
      }
    },
    { immediate: true },
  )

  watch(
    () => route.query,
    async () => {
      page.value = 1
      await execute()
      moreReviews.value = []
      hasMoreReviews.value = true
    },
  )

  async function fetchMoreReviews() {
    try {
      page.value += 1
      isFetchMorePending.value = true

      const { next, results } = await fetchReviews(page.value)

      hasMoreReviews.value = !isEmpty(next)
      moreReviews.value = [...moreReviews.value, ...(results || [])]
    } catch (error) {
      page.value -= 1

      if (error instanceof Error) {
        logError(error)
      }
    } finally {
      isFetchMorePending.value = false
    }
  }

  return {
    fetchMoreReviews,
    isInitialFetchPending,
    isFetchMorePending,
    reviewsResponse,
    canFetchMoreReviews,
  }
}
